import { useCallback, useEffect, useId, useRef, useState } from "react";
import { normalizeComposerLinkUrl } from "@/lib/formatChatMessageHtml";

/**
 * Toolbar control: select text in the reply textarea, then attach a URL as markdown [text](url).
 *
 * @param {{
 *   disabled?: boolean,
 *   textareaRef: React.RefObject<HTMLTextAreaElement | null>,
 *   value: string,
 *   onChange: (next: string) => void,
 * }} props
 */
export default function ComposerLinkControl({ disabled = false, textareaRef, value, onChange }) {
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [needsLabel, setNeedsLabel] = useState(false);
  const [error, setError] = useState("");
  const selectionRef = useRef({ start: 0, end: 0 });
  const panelRef = useRef(null);
  const urlInputRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false);
    setUrl("");
    setLabel("");
    setNeedsLabel(false);
    setError("");
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => urlInputRef.current?.focus());
  }, [open]);

  const openPanel = () => {
    if (disabled) return;
    const ta = textareaRef?.current;
    const start = ta?.selectionStart ?? value.length;
    const end = ta?.selectionEnd ?? value.length;
    const selected = value.slice(start, end);
    selectionRef.current = { start, end };
    const hasSelection = Boolean(selected.trim());
    setNeedsLabel(!hasSelection);
    setLabel(hasSelection ? selected : "");
    setUrl("");
    setError("");
    setOpen(true);
  };

  const apply = () => {
    const href = normalizeComposerLinkUrl(url);
    if (!href) {
      setError("Enter a valid http(s) or mailto link");
      return;
    }
    const { start, end } = selectionRef.current;
    const selected = value.slice(start, end);
    const linkLabel = (needsLabel ? label : selected).trim() || "link";
    const md = `[${linkLabel}](${href})`;
    const next = value.slice(0, start) + md + value.slice(end);
    onChange(next);
    close();
    requestAnimationFrame(() => {
      const ta = textareaRef?.current;
      if (!ta) return;
      ta.focus();
      const pos = start + md.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        title="Add link to selected text"
        aria-label="Add link"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        disabled={disabled}
        onClick={() => (open ? close() : openPanel())}
        className="ui-btn-icon !h-8 !w-8 text-slate-400 hover:text-teal-600 disabled:opacity-40"
      >
        <i className="ri-link text-base" aria-hidden />
      </button>
      {open ? (
        <div
          id={panelId}
          role="dialog"
          aria-label="Insert link"
          className="absolute bottom-full left-0 z-40 mb-2 w-72 rounded-xl border border-slate-200 bg-white p-3 shadow-lg dark:border-slate-700 dark:bg-slate-900"
        >
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {needsLabel ? "Insert link" : "Link selected text"}
          </p>
          {needsLabel ? (
            <label className="mb-2 block">
              <span className="mb-1 block text-[11px] font-medium text-slate-600 dark:text-slate-300">
                Display text
              </span>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Click here"
                className="ui-input-subtle w-full text-xs"
              />
            </label>
          ) : null}
          <label className="mb-2 block">
            <span className="mb-1 block text-[11px] font-medium text-slate-600 dark:text-slate-300">URL</span>
            <input
              ref={urlInputRef}
              type="url"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                if (error) setError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  apply();
                }
              }}
              placeholder="https://example.com"
              className="ui-input-subtle w-full text-xs"
            />
          </label>
          {error ? <p className="mb-2 text-[11px] font-medium text-rose-600">{error}</p> : null}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={close}
              className="rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={apply}
              className="rounded-lg bg-teal-600 px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-teal-700"
            >
              Apply
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
